import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  ConfirmVendorAccountCollectionFlowInput,
  VendorAccountCollectionFlowConfirmationResponse,
} from 'src/queries/vendors/vendors.types'

const CONFIRMATION_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const

export type ConfirmationErrorKind =
  | 'stale-or-terminal'
  | 'not-found'
  | 'acknowledgement-required'
  | 'provider'
  | 'unexpected'

type ConfirmedVendorAccountCollectionFlowResponse = Extract<
  VendorAccountCollectionFlowConfirmationResponse,
  { status: 'CONFIRMED' }
>

export interface UseVendorAccountConfirmationResult {
  startConfirmation: (input: ConfirmVendorAccountCollectionFlowInput) => void
  phase: 'idle' | 'confirming' | 'pending' | 'error'
  errorKind: ConfirmationErrorKind | null
  isInFlight: boolean
  canRetry: boolean
  retry: () => void
  reset: () => void
}

export interface UseVendorAccountConfirmationArgs {
  confirmFlow: (
    input: ConfirmVendorAccountCollectionFlowInput,
  ) => Promise<VendorAccountCollectionFlowConfirmationResponse>
  onConfirmed: (response: ConfirmedVendorAccountCollectionFlowResponse) => void
}

function errorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined

  if ('status' in error && typeof error.status === 'number') {
    return error.status
  }

  if (
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

function errorKindFor(error: unknown): ConfirmationErrorKind {
  switch (errorStatus(error)) {
    case 409:
      return 'stale-or-terminal'
    case 404:
      return 'not-found'
    case 422:
      return 'acknowledgement-required'
    case 502:
      return 'provider'
    default:
      return 'unexpected'
  }
}

function isConfirmedAndPayable(
  response: VendorAccountCollectionFlowConfirmationResponse,
): response is ConfirmedVendorAccountCollectionFlowResponse {
  return (
    response.status === 'CONFIRMED' &&
    typeof response.paymentMethod?.id === 'string' &&
    response.paymentMethod.id.trim() !== '' &&
    response.paymentMethod.type === 'ACH' &&
    response.paymentMethod.status === 'PAYABLE'
  )
}

/**
 * Owns the post-consent Vendor confirmation sequence. Nothing starts until
 * `startConfirmation` is called by the flyout's explicit primary action.
 */
export default function useVendorAccountConfirmation({
  confirmFlow,
  onConfirmed,
}: UseVendorAccountConfirmationArgs): UseVendorAccountConfirmationResult {
  const [phase, setPhase] =
    useState<UseVendorAccountConfirmationResult['phase']>('idle')
  const [errorKind, setErrorKind] = useState<ConfirmationErrorKind | null>(null)
  const [isInFlight, setIsInFlight] = useState(false)
  const [canRetry, setCanRetry] = useState(false)

  const callbacksRef = useRef({ confirmFlow, onConfirmed })
  useLayoutEffect(() => {
    callbacksRef.current = { confirmFlow, onConfirmed }
  })

  const mountedRef = useRef(true)
  const sequenceIdRef = useRef(0)
  const sequenceStartedRef = useRef(false)
  const requestRef = useRef<ConfirmVendorAccountCollectionFlowInput | null>(
    null,
  )
  const inFlightRef = useRef(false)
  const canRetryRef = useRef(false)
  const retryIndexRef = useRef(0)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearRetryTimeout = useCallback((): void => {
    if (retryTimeoutRef.current === null) return
    clearTimeout(retryTimeoutRef.current)
    retryTimeoutRef.current = null
  }, [])

  const updateCanRetry = useCallback((value: boolean): void => {
    canRetryRef.current = value
    setCanRetry(value)
  }, [])

  const attemptConfirmation = useCallback(
    async function attempt(sequenceId: number): Promise<void> {
      const request = requestRef.current
      if (
        !mountedRef.current ||
        sequenceId !== sequenceIdRef.current ||
        !request ||
        inFlightRef.current
      ) {
        return
      }

      inFlightRef.current = true
      setIsInFlight(true)
      setPhase('confirming')
      setErrorKind(null)
      updateCanRetry(false)

      let response: VendorAccountCollectionFlowConfirmationResponse
      try {
        response = await callbacksRef.current.confirmFlow(request)
      } catch (error) {
        if (!mountedRef.current || sequenceId !== sequenceIdRef.current) {
          return
        }

        const nextErrorKind = errorKindFor(error)
        setErrorKind(nextErrorKind)
        setPhase('error')
        updateCanRetry(nextErrorKind !== 'stale-or-terminal')
        return
      } finally {
        if (mountedRef.current && sequenceId === sequenceIdRef.current) {
          inFlightRef.current = false
          setIsInFlight(false)
        }
      }

      if (!mountedRef.current || sequenceId !== sequenceIdRef.current) return

      if (response.status === 'PENDING') {
        setPhase('pending')
        const nextDelay = CONFIRMATION_RETRY_DELAYS_MS[retryIndexRef.current]
        if (nextDelay === undefined) {
          updateCanRetry(true)
          return
        }

        retryIndexRef.current += 1
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null
          void attempt(sequenceId)
        }, nextDelay)
        return
      }

      if (!isConfirmedAndPayable(response)) {
        setErrorKind('unexpected')
        setPhase('error')
        updateCanRetry(true)
        return
      }

      setPhase('idle')
      setErrorKind(null)
      updateCanRetry(false)
      callbacksRef.current.onConfirmed(response)
    },
    [updateCanRetry],
  )

  const startConfirmation = useCallback(
    (input: ConfirmVendorAccountCollectionFlowInput): void => {
      if (sequenceStartedRef.current) return

      sequenceStartedRef.current = true
      requestRef.current = input
      retryIndexRef.current = 0
      clearRetryTimeout()
      const sequenceId = sequenceIdRef.current + 1
      sequenceIdRef.current = sequenceId
      void attemptConfirmation(sequenceId)
    },
    [attemptConfirmation, clearRetryTimeout],
  )

  const retry = useCallback((): void => {
    if (!requestRef.current || inFlightRef.current || !canRetryRef.current) {
      return
    }

    clearRetryTimeout()
    retryIndexRef.current = 0
    updateCanRetry(false)
    const sequenceId = sequenceIdRef.current + 1
    sequenceIdRef.current = sequenceId
    void attemptConfirmation(sequenceId)
  }, [attemptConfirmation, clearRetryTimeout, updateCanRetry])

  const reset = useCallback((): void => {
    sequenceIdRef.current += 1
    clearRetryTimeout()
    sequenceStartedRef.current = false
    requestRef.current = null
    inFlightRef.current = false
    retryIndexRef.current = 0
    canRetryRef.current = false
    setPhase('idle')
    setErrorKind(null)
    setIsInFlight(false)
    setCanRetry(false)
  }, [clearRetryTimeout])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      sequenceIdRef.current += 1
      clearRetryTimeout()
    }
  }, [clearRetryTimeout])

  return {
    startConfirmation,
    phase,
    errorKind,
    isInFlight,
    canRetry,
    retry,
    reset,
  }
}
